import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Blocks, Cable, LayoutGrid, Radio, MessageCircle, Target } from "lucide-react";
import { Card, CardContent } from "../../components/ui";

type Item = {
  key: "kanban" | "omnichannel" | "integrations" | "webhooks" | "distribution";
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  disabled?: boolean;
  badge?: string;
};

export const LeadsSettingsHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const items: Item[] = [
    {
      key: "kanban",
      title: "Kanban",
      description: t("leadsSettingsHub.cards.kanban.description"),
      icon: LayoutGrid,
      path: "/leads/settings/kanban",
    },
    {
      key: "omnichannel",
      title: "Omnichannel",
      description: t("leadsSettingsHub.cards.omnichannel.description"),
      icon: Radio,
      disabled: true,
      badge: t("leadsSettingsHub.comingSoon"),
    },
    {
      key: "integrations",
      title: "Integrações",
      description: t("leadsSettingsHub.cards.integrations.description"),
      icon: Cable,
      path: "/marketplace",
    },
    {
      key: "webhooks",
      title: "Webhooks",
      description: t("leadsSettingsHub.cards.webhooks.description"),
      icon: Blocks,
      path: "/leads/settings/webhooks",
    },
    {
      key: "distribution",
      title: "Distribuição",
      description: t("leadsSettingsHub.cards.distribution.description"),
      icon: Target,
      path: "/leads/settings/distribution",
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t("leadsSettingsHub.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("leadsSettingsHub.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <Card
              key={item.title}
              className={item.disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:shadow-md transition-shadow"}
              onClick={() => {
                if (!item.disabled && item.path) navigate(item.path);
              }}
            >
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  {item.badge ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground">{item.badge}</span>
                  ) : null}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{t(`leadsSettingsHub.cards.${item.key}.title`)}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
